import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Input, Checkbox } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { FiAlertCircle } from "react-icons/fi";
import ColorPicker from "components/common/ColorPicker/ColorPicker";
import SubmitButton from "components/common/SubmitButton";
import { useActiveDepartments } from "services/departmentService";
import { useActiveWorkTypes } from "services/workTypeService";
import styles from "../Settings.module.scss";

const getWorkTypeId = (item) => item?.workTypeId ?? null;

const getFormValues = (valueGetter, includeActive) => ({
  name: valueGetter("name"),
  colour: valueGetter("colour"),
  ...(includeActive ? { active: valueGetter("active") } : {}),
});

const EditActivityTag = (props) => {
  const ADD_NEW = props.type === "addNew";
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [allWorkTypes, setAllWorkTypes] = useState(true);
  const [department, setDepartment] = useState(null);
  const [workType, setWorkType] = useState(null);

  const { data: departments, loading: loadingDepartments } =
    useActiveDepartments();
  const { data: workTypes, loading: loadingWorkTypes } = useActiveWorkTypes();

  const workTypeOptions = useMemo(() => {
    if (!department?.departmentID) return [];
    return (workTypes || []).filter(
      (wt) => wt.departmentId === department.departmentID,
    );
  }, [department?.departmentID, workTypes]);

  useEffect(() => {
    if (!props.item) {
      setAllWorkTypes(true);
      setDepartment(null);
      setWorkType(null);
      return;
    }

    const workTypeId = getWorkTypeId(props.item);
    if (workTypeId == null) {
      setAllWorkTypes(true);
      setDepartment(null);
      setWorkType(null);
      return;
    }

    const matchedWorkType = (workTypes || []).find(
      (wt) => wt.workTypeID === workTypeId,
    );
    if (!matchedWorkType) {
      setAllWorkTypes(false);
      setDepartment(null);
      setWorkType(null);
      return;
    }

    setAllWorkTypes(false);
    setWorkType(matchedWorkType);
    setDepartment(
      (departments || []).find(
        (dept) => dept.departmentID === matchedWorkType.departmentId,
      ) || null,
    );
  }, [props.item, workTypes, departments]);

  const validator = (values) => {
    const errors = {};
    if (!values.name?.trim()) {
      errors.name = "Name is required";
    }
    return errors;
  };

  const handleSubmit = async (values) => {
    if (
      !allWorkTypes &&
      (!department?.departmentID || !workType?.workTypeID)
    ) {
      return;
    }

    const submission = {
      ...values,
      workTypeId: allWorkTypes ? null : workType?.workTypeID ?? null,
    };

    setIsSubmitting(true);
    try {
      await props.onSubmit?.(submission);
    } finally {
      setIsSubmitting(false);
    }
  };

  const initialValues = {
    name: props.item?.name || "",
    colour: props.item?.colour || "none",
    active: props.item?.active ?? true,
  };

  const initialWorkTypeId = ADD_NEW ? null : getWorkTypeId(props.item);
  const initialAllWorkTypes = ADD_NEW ? true : initialWorkTypeId == null;
  const currentWorkTypeId = allWorkTypes ? null : workType?.workTypeID ?? null;
  const workTypeSelectionChanged =
    allWorkTypes !== initialAllWorkTypes ||
    currentWorkTypeId !== initialWorkTypeId;
  const workTypeSelectionValid =
    allWorkTypes || (!!department?.departmentID && !!workType?.workTypeID);

  return (
    <Dialog
      title={ADD_NEW ? "Add Activity Tags" : `Edit ${props.item?.name}`}
      onClose={props.cancelEdit}
      width={600}
    >
      <Form
        ignoreModified
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validator={validator}
        render={(formRenderProps) => (
          <>
            <FormElement style={{ maxWidth: 1200 }}>
              <fieldset className="p-3 ps-4">
                <div
                  className={`mb-2 ${isSubmitted && formRenderProps.errors?.name
                    ? "validation-invalid"
                    : ""
                    }`}
                >
                  <Label className="label my-1 w-100">Activity Name</Label>
                  <Field name="name" component={Input} className="mb-1" />
                </div>

                <Checkbox
                  label="All Work Types"
                  className="my-2"
                  value={allWorkTypes}
                  onChange={(e) => {
                    setAllWorkTypes(e.value);
                    setIsSubmitted(false);
                    if (e.value) {
                      setDepartment(null);
                      setWorkType(null);
                    }
                  }}
                />

                <div className="d-flex align-items-start gap-3 mb-2 mt-1 w-50">
                  <div
                    className={
                      isSubmitted && !allWorkTypes && !department?.departmentID
                        ? "validation-invalid"
                        : ""
                    }
                  >
                    <Label className="label my-1 w-100">Department</Label>
                    <ComboBox
                      data={departments || []}
                      className={styles["work-type-field-style"]}
                      style={{ width: "230px" }}
                      textField="name"
                      dataItemKey="departmentID"
                      value={department}
                      onChange={(e) => {
                        setDepartment(e.value || null);
                        setWorkType(null);
                        setIsSubmitted(false);
                      }}
                      placeholder="Select Department"
                      disabled={allWorkTypes || loadingDepartments}
                      loading={loadingDepartments}
                      iconClassName="wa"
                    />
                  </div>

                  <div className="d-flex align-items-start gap-2 align-items-center justify-content-center">
                    <div
                      className={
                        isSubmitted && !allWorkTypes && !workType?.workTypeID
                          ? "validation-invalid"
                          : ""
                      }
                    >
                      <Label className="label my-1 w-100">Work Type</Label>
                      <ComboBox
                        data={workTypeOptions}
                        className={styles["work-type-field-style"]}
                        style={{ width: "230px" }}
                        textField="name"
                        dataItemKey="workTypeID"
                        value={workType}
                        onChange={(e) => setWorkType(e.value || null)}
                        placeholder="Select Work Type"
                        disabled={
                          allWorkTypes ||
                          !department ||
                          loadingWorkTypes
                        }
                        loading={loadingWorkTypes}
                        iconClassName="wa"
                      />
                    </div>
                    <Tooltip anchorElement="target" position="top">
                      <FiAlertCircle
                        title="Select Department to see corresponding work-types"
                        className="mt-4 cursor-pointer"
                        style={{ height: "20px", width: "20px" }}
                      />
                    </Tooltip>
                  </div>
                </div>

                <div className="mb-2">
                  <Label className="label my-1 w-100">Row Color</Label>
                  <Field name="colour" component={ColorPicker} />
                </div>

                {!ADD_NEW && (
                  <div className="d-inline-flex align-items-center mt-1 me-3">
                    <Field
                      name="active"
                      component={Checkbox}
                      label="Active"
                      type="checkbox"
                    />
                  </div>
                )}
              </fieldset>
            </FormElement>

            <div className="dialog-footer">
              <button
                className="bgBtnSecondary common-btn me-2"
                onClick={props.cancelEdit}
                type="button"
              >
                Cancel
              </button>
              <SubmitButton
                type="button"
                className="me-3"
                onClick={() => {
                  setIsSubmitted(true);
                  if (!formRenderProps.valid || !workTypeSelectionValid) return;
                  handleSubmit(getFormValues(formRenderProps.valueGetter, !ADD_NEW));
                }}
                isLoading={isSubmitting}
                disabled={!formRenderProps.modified && !workTypeSelectionChanged}
              >
                {ADD_NEW ? "Save" : "Update"}
              </SubmitButton>
            </div>
          </>
        )}
      />
    </Dialog>
  );
};

export default EditActivityTag;
